import { useEffect, useRef, useState } from 'react';

import { useFormikContext } from 'formik';

import { getTipPercentageError, resolveTipPercentages } from 'lib/constants/transaction/tips';

import Input from 'components/shared/Input/Input';
import Switch from 'components/shared/Switch/Switch';

const PRESET_LABELS = ['Option 1', 'Option 2', 'Option 3'];

const TipSettings = () => {
  const { setFieldValue, values } = useFormikContext();
  const percentages = resolveTipPercentages(values?.displaySettings);
  const [inputs, setInputs] = useState(() => percentages.map(String));
  const [errors, setErrors] = useState([]);
  const savedRef = useRef(percentages);

  useEffect(() => {
    const changed = percentages.map((value, i) => value !== savedRef.current[i]);
    savedRef.current = percentages;
    setInputs((current) =>
      current.map((value, i) => (changed[i] ? String(percentages[i]) : value))
    );
    setErrors((current) => current.map((error, i) => (changed[i] ? null : error)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [percentages.join(',')]);

  const updateDisplaySettings = (patch) =>
    setFieldValue('displaySettings', { ...values?.displaySettings, ...patch });

  const handleAllowTipsChange = (checked) => {
    updateDisplaySettings({ allow_tips: checked, tip_percentages: percentages });
    setFieldValue('allowTips', checked);
  };

  const setErrorAt = (index, error) =>
    setErrors((current) => {
      const next = [...current];
      next[index] = error;
      return next;
    });

  const handleInputChange = (index, next) => {
    setInputs((current) => current.map((value, i) => (i === index ? next : value)));
    if (errors[index]) setErrorAt(index, getTipPercentageError(next));
  };

  const handleInputBlur = (index) => {
    const error = getTipPercentageError(inputs[index]);
    setErrorAt(index, error);
    if (error) return;

    const nextPercentage = Math.round(parseFloat(inputs[index]) * 100) / 100;

    setInputs((current) =>
      current.map((value, i) => (i === index ? String(nextPercentage) : value))
    );
    updateDisplaySettings({
      tip_percentages: percentages.map((value, i) => (i === index ? nextPercentage : value))
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <p className="text-base font-500 leading-6 text-neutral-900">Gratuity</p>
        <p className="text-sm leading-[22px] text-neutral-700">
          Let patients add a tip when they pay. The options below appear in the Add gratuity section
          of every payment.
        </p>
      </div>
      <div className="flex items-center gap-2">
        <Switch
          checked={values?.allowTips}
          onChange={handleAllowTipsChange}
          id="allow-tips-switch"
        />
        <label htmlFor="allow-tips-switch" className="text-sm font-400 text-neutral-900">
          Offer gratuity on payments
        </label>
      </div>

      {values?.allowTips && (
        <div className="flex flex-col gap-3">
          <p className="text-base font-500 leading-6 text-neutral-900">Suggested tip percentages</p>
          <div className="flex flex-col gap-4">
            {inputs.map((value, index) => {
              const id = `tip-percentage-${index}`;
              return (
                <div key={id} className="flex items-center gap-4">
                  <label htmlFor={id} className="w-14 text-sm text-neutral-700">
                    {PRESET_LABELS[index]}
                  </label>
                  <Input
                    id={id}
                    value={value}
                    onChange={(event) => handleInputChange(index, event.target.value)}
                    onBlur={() => handleInputBlur(index)}
                    type="text"
                    inputMode="decimal"
                    rightText="%"
                    error={errors[index]}
                    className="w-[120px]"
                    data-qa={id}
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default TipSettings;
